import { HTMLAttributes } from 'react';

import { ServiceCode, UserRequestChannel, UserRequestStatus } from '@/shared/types';

import { ErzPermit } from '@/modules/erz/pages/services/ErzExtract';
import { IACUserRequestChannel } from '@/modules/iac/types';

import { PaymentReceipt, StatusDecorator, Weapon_v2, WeaponStatuses } from '@/api/types';

import { InformerProps } from '../../Informer';
import { InsuranceOrderCardProps } from '../children/InsuranceOrderCard';
import { UserRequestCardProps } from '../children/UserRequestCard';
import { UserRequestDraftCardProps } from '../children/UserRequestDraftCard';
import { UserRequestPartCardProps } from '../children/UserRequestPartCard';

export enum ServiceRowCardVariant {
  COMPLAINT = 'complaint',
  RECEIPT = 'receipt',
  EXTRACT = 'extract',
  PERMIT = 'permit',
  CONTRACT = 'contract',
  USER_REQUEST = 'user_request',
  USER_REQUEST_PART = 'user_request_part',
  USER_REQUEST_DRAFT = 'user_request_draft',
  WEAPON_REGISTERED = 'weapon_registered',
  WEAPON_PART = 'weapon_part',
  INSURANCE_ORDER = 'insurance_order',
}

export type ExtractRequestChannel = UserRequestChannel | IACUserRequestChannel;

interface CommonCardData {
  id?: string;
}

interface ComplaintData extends CommonCardData {
  title: string;
  requestNumber: string;
  createdAt: Date | string;
  complainDescription: string;
}

type ReceiptData = CommonCardData & Partial<Omit<PaymentReceipt, 'id' | 'status_decorator'>>;

interface ExtractData extends CommonCardData {
  title: string;
  requestNumber: string;
  requestDate: Date | string;
  extractDate: Date | string;
  extractPurpose: string;
  artifacts: string[];
}

interface ContractData extends CommonCardData {
  title: string;
  contractNumber: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  endDate: Date | string;
}

export interface DraftData extends Required<CommonCardData> {
  title: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  currentStep: number;
  totalSteps: number;
  serviceCode: ServiceCode;
}

export interface BaseServiceRowCardProps extends HTMLAttributes<HTMLDivElement> {
  variant: ServiceRowCardVariant;
  informerData?: Nullable<InformerProps>;
}

export interface ComplaintCardProps extends BaseServiceRowCardProps {
  variant: ServiceRowCardVariant.COMPLAINT;
  status: UserRequestStatus;
  data: ComplaintData;
  onDownloadAppeal?: (attrs: any) => void;
}

export interface ReceiptCardProps extends BaseServiceRowCardProps {
  variant: ServiceRowCardVariant.RECEIPT;
  data: ReceiptData;
  onDownloadReceipt?: (invoiceId: string) => void;
}

export interface ExtractCardProps extends BaseServiceRowCardProps {
  variant: ServiceRowCardVariant.EXTRACT;
  status: UserRequestStatus | StatusDecorator;
  channel: ExtractRequestChannel;
  channels: Partial<Record<ExtractRequestChannel, string>>;
  data: ExtractData;
  onDownloadSignatureFile?: (attrs: any) => void;
  onDownloadExtract?: (attrs: any) => void;
}

export interface PermitCardProps extends BaseServiceRowCardProps {
  variant: ServiceRowCardVariant.PERMIT;
  data: ErzPermit;
  onDownloadExtract?: () => void;
  onDeletePermit?: () => void;
}

export interface ContractCardProps extends BaseServiceRowCardProps {
  variant: ServiceRowCardVariant.CONTRACT;
  data: ContractData;
  onDownloadContract?: (attrs: any) => void;
}

export interface WeaponRegisteredCardProps extends BaseServiceRowCardProps {
  variant: ServiceRowCardVariant.WEAPON_REGISTERED;
  data: Weapon_v2;
  onDetailedClick: () => void;
}

export interface WeaponPartCardProps extends BaseServiceRowCardProps {
  variant: ServiceRowCardVariant.WEAPON_PART;
  status: WeaponStatuses;
  data?: {
    type: Nullable<string>;
    caliber: Nullable<string>;
    serialNum: Nullable<string>;
    brandName: Nullable<string>;
    vendor?: Nullable<string>;
  };
}

export type ServiceRowCardProps =
  | ComplaintCardProps
  | ReceiptCardProps
  | ExtractCardProps
  | PermitCardProps
  | ContractCardProps
  | UserRequestCardProps
  | UserRequestPartCardProps
  | UserRequestDraftCardProps
  | WeaponRegisteredCardProps
  | WeaponPartCardProps
  | InsuranceOrderCardProps;
